const BASE = (import.meta.env.VITE_API_BASE as string | undefined) || "https://otp.mioe.com.tr";
// Derived brand/host text so logo titles etc. stay correct across deployments
// (this codebase runs at more than one live domain — the Cloudflare Worker and
// the single-machine box — see docs/AWS-ISTANBUL-SINGLE-MACHINE.md) without
// hardcoding a second literal that could drift from BASE.
export const SITE_HOST = new URL(BASE).host;

export interface Webapp {
  id: string;
  code: string;
  code_hidden: boolean;
  name: string;
  callback_url: string;
  inbound_number: string | null;
  has_webhook_key: boolean;
  webhook_url_template: string;
  active: boolean;
  verified_count: number;
  free_quota: number;
  paid_quota: number;
  unlimited: boolean;
  quota_allowance: number | null;
  quota_remaining: number | null;
  billing_status: "unlimited" | "subscribed" | "trial" | "trial_exhausted";
  // "account": a customer-owned app; allowance/remaining/status are the owner's
  // shared credit pool and free_quota/paid_quota are not used by the gate.
  quota_source: "account" | "webapp";
  owner_customer_id: string | null;
  owner_suspended: boolean;
  created_at: number;
  updated_at: number;
}

export interface Admin {
  id: string;
  email: string;
  phone: string | null;
  created_at: number;
  last_login_at: number | null;
}

export interface Customer {
  id: string;
  email: string;
  email_verified: boolean;
  phone: string | null;
  trial_credits: number;
  paid_credits: number;
  consumed_credits: number;
  credit_allowance: number;
  credits_remaining: number;
  webapp_count: number;
  created_at: number;
  last_login_at: number | null;
  stage: CustomerStage;
  funnel: {
    verify_mails: number;
    last_verify_mail_at: number | null;
    webapps_ever: number;
    first_webapp_at: number | null;
    otp_issued: number;
    otp_verified: number;
    last_otp_day: string | null;
  };
  // Current user agreement (/kosullar) and when this customer accepted it;
  // null = not accepted, so the portal is blocked for them.
  agreement_version: string;
  agreement_accepted_at: number | null;
  // Operator-only suspension; null = in good standing. The reason is an
  // internal note (the customer only sees it in their own data export).
  suspended_at: number | null;
  suspended_reason: string | null;
  suspended_by: string | null;
}

// Furthest onboarding step reached; see api/src/routes/admin/customers.ts.
export type CustomerStage =
  | "active"
  | "email_unverified"
  | "no_webapp"
  | "not_integrated"
  | "no_verification";

export interface CustomerWebapp {
  id: string;
  code: string;
  name: string;
  active: boolean;
  unlimited: boolean;
  verified_count: number;
  deleted: boolean;
  created_at: number;
}

export interface SmsLoginInit {
  sessionId: string;
  // One-time token from /login/sms/init. Held in memory only (never stored,
  // never in a URL) and posted back to claim the verified session — the API
  // will not mint a cookie without it.
  pollToken: string;
  code: string;
  otp: string;
  inboundNumber?: string;
  expiresAt: string;
  expiresIn: number;
}

// Build a cross-platform "sms:" deep link prefilled with the body text.
// "?&body=" is the form both iOS and Android honor.
export function smsDeepLink(inboundNumber: string | undefined, body: string): string | null {
  if (!inboundNumber) return null;
  const number = inboundNumber.replace(/[^0-9+]/g, "");
  if (!number) return null;
  return `sms:${number}?&body=${encodeURIComponent(body)}`;
}

export type SmsLoginStatus =
  | { status: "pending" }
  | { status: "timeout" }
  | { status: "failed" }
  | { status: "no_account" }
  | { status: "verified"; id: string; email: string };

export interface OtpSession {
  id: string;
  phone: string;
  status: "pending" | "verified" | "timeout" | "failed";
  created_at: number;
  expires_at: number;
  resolved_at: number | null;
  callback_status: number | null;
  callback_error: string | null;
}

export interface Stats {
  range_days: number;
  since: string;
  totals: {
    webapps: number;
    active_webapps: number;
    customers: number;
    lifetime_verified: number;
    credits_consumed: number;
    credits_remaining: number;
  };
  funnel: {
    issued: number;
    verified: number;
    timeout: number;
    failed: number;
    success_rate: number | null;
  };
  health: {
    callback_errors: number;
    avg_verify_ms: number | null;
    inbound_total: number;
    inbound_matched: number;
    inbound_unmatched: number;
    forbidden: number;
  };
  daily: Array<{
    day: string;
    issued: number;
    verified: number;
    timeout: number;
    failed: number;
    callback_errors: number;
  }>;
  webhook_results: Array<{ result: string; count: number }>;
  top_webapps: Array<{
    webapp_id: string;
    code: string | null;
    name: string | null;
    issued: number;
    verified: number;
    timeout: number;
    failed: number;
  }>;
  top_customers: Array<{
    id: string;
    email: string;
    verified: number;
    consumed_credits: number;
  }>;
  revenue: {
    paid_orders: number;
    gross_kurus: number;
    by_day: Array<{ day: string; orders: number; kurus: number }>;
  };
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...(init.headers || {}),
    },
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const msg = (data && (data.error as string)) || `http_${res.status}`;
    throw new Error(msg);
  }
  return data as T;
}

export const api = {
  me: () => request<{ id: string; email: string }>("/admin/me"),
  login: (email: string, password: string) =>
    request<{ id: string; email: string }>("/admin/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  smsLoginInit: (phone: string) =>
    request<SmsLoginInit>("/admin/login/sms/init", {
      method: "POST",
      body: JSON.stringify({ phone }),
    }),
  smsLoginStatus: (sessionId: string, pollToken: string) =>
    request<SmsLoginStatus>(`/admin/login/sms/status/${sessionId}`, {
      method: "POST",
      body: JSON.stringify({ pollToken }),
    }),
  logout: () => request<{ ok: true }>("/admin/logout", { method: "POST" }),
  requestPasswordReset: (email: string) =>
    request<{ ok: true }>("/admin/password-reset/request", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  confirmPasswordReset: (token: string, password: string) =>
    request<{ ok: true }>("/admin/password-reset/confirm", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    }),

  stats: (days = 30) => request<Stats>(`/admin/stats?days=${days}`),

  listWebapps: () => request<{ items: Webapp[] }>("/admin/webapps"),
  createWebapp: (body: {
    name: string;
    callback_url: string;
    code?: string;
    inbound_number?: string | null;
  }) =>
    request<
      Webapp & { api_key: string; hmac_secret: string; webhook_url: string }
    >("/admin/webapps", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateWebapp: (
    id: string,
    body: Partial<
      Pick<
        Webapp,
        | "name"
        | "callback_url"
        | "active"
        | "inbound_number"
        | "free_quota"
        | "paid_quota"
        | "unlimited"
        | "code_hidden"
        | "owner_customer_id"
      >
    >,
  ) =>
    request<Webapp>(`/admin/webapps/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteWebapp: (id: string) =>
    request<{ ok: true }>(`/admin/webapps/${id}`, { method: "DELETE" }),
  rotateApiKey: (id: string) =>
    request<{ api_key: string }>(`/admin/webapps/${id}/rotate-api-key`, { method: "POST" }),
  rotateHmac: (id: string) =>
    request<{ hmac_secret: string }>(`/admin/webapps/${id}/rotate-hmac`, { method: "POST" }),
  rotateWebhookKey: (id: string) =>
    request<{ webhook_url: string }>(`/admin/webapps/${id}/rotate-webhook-key`, {
      method: "POST",
    }),
  webappSessions: (id: string) =>
    request<{ items: OtpSession[] }>(`/admin/webapps/${id}/sessions`),

  listCustomers: () => request<{ items: Customer[] }>("/admin/customers"),
  customerWebapps: (id: string) =>
    request<{ items: CustomerWebapp[] }>(`/admin/customers/${id}/webapps`),
  updateCustomer: (
    id: string,
    body: Partial<Pick<Customer, "trial_credits" | "paid_credits" | "email_verified">>,
  ) => request<Customer>(`/admin/customers/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteCustomer: (id: string) =>
    request<{ ok: true }>(`/admin/customers/${id}`, { method: "DELETE" }),
  suspendCustomer: (id: string, reason: string) =>
    request<Customer & { sessions_failed: number }>(`/admin/customers/${id}/suspend`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  reinstateCustomer: (id: string) =>
    request<Customer>(`/admin/customers/${id}/reinstate`, { method: "POST" }),

  listAdmins: () => request<{ items: Admin[] }>("/admin/admins"),
  createAdmin: (body: { email: string; password: string; phone?: string | null }) =>
    request<Admin>("/admin/admins", { method: "POST", body: JSON.stringify(body) }),
  setAdminPassword: (id: string, password: string) =>
    request<{ ok: true }>(`/admin/admins/${id}/password`, {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
  setAdminPhone: (id: string, phone: string | null) =>
    request<Admin>(`/admin/admins/${id}/phone`, {
      method: "POST",
      body: JSON.stringify({ phone }),
    }),
  deleteAdmin: (id: string) =>
    request<{ ok: true }>(`/admin/admins/${id}`, { method: "DELETE" }),
};

export function fmtTime(ms: number | null | undefined): string {
  if (!ms) return "—";
  const d = new Date(ms);
  return d.toLocaleString();
}
